// UnassignedRosterPanel.js
import { useEffect, useMemo, useRef, useState } from "react";
import { FaCheckCircle, FaFilter, FaQuestionCircle } from "react-icons/fa";

export default function UnassignedRosterPanel({
  roster = [],
  attendanceByUser = {},
  assignedUserIds = new Set(),
  onDragStartUser,
  canEdit = true,
  squadOptions = [],
  positionOptions = [],
  positionColors = {},
  coachTeam = null,
  teamFilter = null,
  squadFilter = null,
}) {
  const absentIcon = (
    <span className="statusIcon absent custom-absent" style={{ width: 14, height: 14 }}>
      <span className="absent-icon-inner">×</span>
    </span>
  );
  const [query, setQuery] = useState("");
  const [team, setTeam] = useState(() => teamFilter || coachTeam || "all");
  const [squad, setSquad] = useState(() => squadFilter || "all");

  useEffect(() => {
    setTeam(teamFilter || coachTeam || "all");
  }, [teamFilter, coachTeam]);

  useEffect(() => {
    setSquad(squadFilter || "all");
  }, [squadFilter]);

  const [position, setPosition] = useState("all");
  const [ageGroup, setAgeGroup] = useState("all");
  const [avail, setAvail] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const panelRef = useRef(null);

  // Derive option lists
  const rosterSquads = useMemo(() => {
    const s = new Set();
    roster.forEach((r) => r.squad && s.add(String(r.squad)));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [roster]);

  const availableSquads = useMemo(() => {
    const provided = (Array.isArray(squadOptions) ? squadOptions : [])
      .map((s) => String(s).trim())
      .filter(Boolean);
    if (provided.length) {
      return Array.from(new Set(provided)).sort((a, b) => a.localeCompare(b));
    }
    return rosterSquads;
  }, [squadOptions, rosterSquads]);

  const rosterPositions = useMemo(() => {
    const p = new Set();
    roster.forEach((r) => {
      const normalized = cleanPosition(r.position);
      if (normalized) p.add(normalized);
    });
    return Array.from(p).sort((a, b) => a.localeCompare(b));
  }, [roster]);

  const availablePositions = useMemo(() => {
    const provided = (Array.isArray(positionOptions) ? positionOptions : [])
      .map((p) => cleanPosition(p))
      .filter(Boolean);
    if (provided.length) {
      return Array.from(new Set(provided)).sort((a, b) => a.localeCompare(b));
    }
    return rosterPositions;
  }, [positionOptions, rosterPositions]);

  const availableAgeGroups = useMemo(() => {
    const ageGroups = new Set();
    roster.forEach((r) => {
      if (r.age_group) {
        ageGroups.add(String(r.age_group).trim());
      }
    });
    return Array.from(ageGroups).sort((a, b) => a.localeCompare(b));
  }, [roster]);

  const availableTeams = useMemo(() => {
    const teams = new Set();
    roster.forEach((r) => {
      if (r.team) {
        teams.add(String(r.team).trim());
      }
    });
    return Array.from(teams).sort((a, b) => a.localeCompare(b));
  }, [roster]);

  // Filtering logic
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return roster
      .filter((r) => {
        const isAssigned = assignedUserIds.has(r.id);
        if (isAssigned) return false;

        if (q && !r.name.toLowerCase().includes(q)) return false;

        const athleteTeam = r.team ? String(r.team).trim() : "";
        if (team !== "all" && athleteTeam !== team) return false;

        const athleteSquad = r.squad ? String(r.squad).trim() : "";
        if (squad !== "all" && athleteSquad !== squad) return false;

        if (position !== "all") {
          const rp = cleanPosition(r.position);
          if (!rp || rp !== position) return false;
        }

        if (ageGroup !== "all") {
          const athleteAgeGroup = r.age_group ? String(r.age_group).trim() : "";
          if (athleteAgeGroup !== ageGroup) return false;
        }

        if (avail !== "all") {
          const st = attendanceByUser[r.id];
          if (avail === "available" && st !== "present") return false;
          if (avail === "unavailable" && st !== "absent") return false;
          if (avail === "noresponse" && st != null) return false;
        }

        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [roster, query, team, squad, position, ageGroup, avail, assignedUserIds, attendanceByUser]);

  // Prevent filter UI from being draggable
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const stop = (e) => e.stopPropagation();
    const nodes = el.querySelectorAll("input, select, textarea, button");
    nodes.forEach((n) => n.addEventListener("dragstart", stop));
    return () => nodes.forEach((n) => n.removeEventListener("dragstart", stop));
  }, []);

  const activeFilterCount = [
    team !== "all",
    squad !== "all",
    position !== "all",
    ageGroup !== "all",
    avail !== "all",
  ].filter(Boolean).length;

  return (
    <div ref={panelRef} className="stack" style={{ minHeight: 0 }}>
      {/* Filters */}
      <div className="filterBar">
        <input
          className="input"
          placeholder="Search name"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ flex: "1 1 200px" }}
        />
        <button
          className={`btn-filter icon-only ${showFilters ? "active" : ""}`}
          onClick={() => setShowFilters(!showFilters)}
          title="Filters"
          aria-label="Filters"
        >
          <FaFilter />
          {activeFilterCount > 0 && <span className="filter-badge">{activeFilterCount}</span>}
        </button>
      </div>

      {showFilters && (
        <div className="filterBarPanel">
          <select className="input filterSelect" value={team} onChange={(e) => setTeam(e.target.value)}>
            <option value="all">All Teams</option>
            {availableTeams.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <select className="input filterSelect" value={squad} onChange={(e) => setSquad(e.target.value)}>
            <option value="all">All Squads</option>
            {availableSquads.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select className="input filterSelect" value={position} onChange={(e) => setPosition(e.target.value)}>
            <option value="all">All Positions</option>
            {availablePositions.map((p) => (
              <option key={p} value={p}>{pLabel(p)}</option>
            ))}
          </select>
          <select className="input filterSelect" value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)}>
            <option value="all">All Age Groups</option>
            {availableAgeGroups.map((ag) => (
              <option key={ag} value={ag}>{ag}</option>
            ))}
          </select>
          <select className="input filterSelect" value={avail} onChange={(e) => setAvail(e.target.value)}>
            <option value="all">All Availability</option>
            <option value="available">Available</option>
            <option value="unavailable">Unavailable</option>
            <option value="noresponse">No Response</option>
          </select>
          <button
            className="btn-clear-filters"
            onClick={() => {
              setTeam(teamFilter || coachTeam || "all");
              setSquad(squadFilter || "all");
              setPosition("all");
              setAgeGroup("all");
              setAvail("all");
            }}
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* List */}
      <div className="stack" style={{ maxHeight: 600, overflow: "auto" }}>
        {filtered.map((u) => {
          const st = attendanceByUser[u.id];
          const posCls = posClass(u.position);
          const baseClass = `chip block ${posCls}`;
          const cssClass = st ? `${baseClass} ${st}` : baseClass;
          const customColor = getPositionColor(u.position, positionColors);

          return (
           <div
  key={u.id}
  className={cssClass}
  draggable={canEdit}
  onDragStart={canEdit ? (e) => handleDragStart(e, u, onDragStartUser) : undefined}
  style={customColor ? { backgroundColor: customColor, borderColor: customColor } : undefined}
  title={`${u.name}${u.position ? " • " + u.position : ""}${u.squad ? " • " + u.squad : ""}${u.age_group ? " • " + u.age_group : ""}${u.team ? " • " + u.team : ""}`}
>
    <div className="row" style={{ justifyContent: "space-between", width: "100%" }}>
      <div className="row" style={{ gap: 8, alignItems: "center" }}>
      {st === "present" ? (
        <FaCheckCircle className="statusIcon present lineup-circle" />
      ) : st === "absent" ? (
        absentIcon
      ) : (
        <FaQuestionCircle className="statusIcon pending" />
      )}
      
      <div className="chipName">{u.name}</div>
      {u.position && <div className="chipMeta">{u.position}</div>}
      {u.squad && <div className="chipMeta">{String(u.squad).toUpperCase()}</div>}
      {u.age_group && <div className="chipMeta">{u.age_group}</div>}
      
    </div>
    
  </div>
</div>
          
          );
        })}
        {filtered.length === 0 && <span className="help">No athletes match your filters.</span>}
      </div>
    </div>
  );
}

/* ---- tiny helpers (same as before in your project) ---- */
function cleanPosition(p) {
  if (!p) return "";
  const s = String(p).toLowerCase();
  if (s.includes("cox")) return "cox";
  if (s.includes("starboard") || s.includes("stbd") || s.includes("sb")) return "starboard";
  if (s.includes("port") || s.includes("pt")) return "port";
  if (s.includes("both")) return "both";
  return s;
}
function pLabel(p) {
  if (p === "cox") return "Cox";
  if (p === "starboard") return "Starboard";
  if (p === "port") return "Port";
  if (p === "both") return "Both";
  return p;
}
function posClass(p) {
  if (!p) return "pos-default";
  const key = String(p).toLowerCase();
  if (key.includes("both")) return "pos-both";
  if (key.includes("port")) return "pos-port";
  if (key.includes("starboard")) return "pos-starboard";
  if (key.includes("cox")) return "pos-cox";
  return "pos-default";
}

function getPositionColor(position, colorMap) {
  if (!position || !colorMap) return null;
  const key = String(position).toLowerCase();
  if (colorMap[key]) return colorMap[key];
  for (const [name, color] of Object.entries(colorMap)) {
    if (key.includes(name) || name.includes(key)) {
      return color;
    }
  }
  return null;
}

function handleDragStart(e, u, onDragStartUser) {
  e.dataTransfer.setData("text/plain", JSON.stringify({ userId: u.id }));
  e.dataTransfer.effectAllowed = "move";
  onDragStartUser?.(u.id);
}
